import { useRef, useState, useCallback } from "react";
import type { InputFile } from "../../state.js";
import { revokeUrl, teardownInputs } from "../../state.js";

const SUPPORTED_EXTS = /\.(png|jpe?g|webp|heic|heif)$/i;
const ACCEPT = ".png,.jpg,.jpeg,.webp,.heic,.heif,image/png,image/jpeg,image/webp,image/heic,image/heif";

const HEIC_PLACEHOLDER =
  "data:image/svg+xml;base64," +
  btoa(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36"><rect width="36" height="36" fill="#e5e7eb"/><text x="18" y="22" text-anchor="middle" font-family="sans-serif" font-size="9" fill="#6b7280">HEIC</text></svg>`,
  );

interface UploadZoneProps {
  inputs: InputFile[];
  disabled: boolean;
  onInputsChange: (next: InputFile[]) => void;
  onError: (msg: string) => void;
}

export function UploadZone({ inputs, disabled, onInputsChange, onError }: UploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragCountRef = useRef(0);

  const handleFiles = useCallback(
    (files: File[]) => {
      const rejected: string[] = [];
      const accepted: InputFile[] = [];
      for (const f of files) {
        if (!SUPPORTED_EXTS.test(f.name) && !f.type.startsWith("image/")) {
          rejected.push(f.name);
          continue;
        }
        const id = crypto.randomUUID?.() ?? String(Math.random()).slice(2);
        const isHeic = /\.(heic|heif)$/i.test(f.name) || f.type === "image/heic" || f.type === "image/heif";
        const thumbUrl = isHeic ? undefined : URL.createObjectURL(f);
        accepted.push({ id, file: f, thumbUrl });
      }
      if (rejected.length > 0) {
        onError(`不支援的檔案類型已跳過: ${rejected.join(", ")}`);
      }
      onInputsChange([...inputs, ...accepted]);
    },
    [inputs, onInputsChange, onError],
  );

  return (
    <>
      <h2>1. 上傳圖片</h2>

      <div
        className={`upload-zone ${dragActive ? "dragover" : ""}`}
        tabIndex={0}
        role="button"
        style={disabled ? { pointerEvents: "none", opacity: 0.5 } : undefined}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          dragCountRef.current++;
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          dragCountRef.current--;
          if (dragCountRef.current === 0) setDragActive(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragCountRef.current = 0;
          setDragActive(false);
          if (e.dataTransfer) handleFiles(Array.from(e.dataTransfer.files));
        }}
      >
        <div>📁 點此選擇圖片,或拖曳到這裡</div>
        <div className="hint">支援 PNG / JPG / WebP / HEIC。可多選。</div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={() => {
            if (fileInputRef.current?.files) {
              handleFiles(Array.from(fileInputRef.current.files));
              fileInputRef.current.value = "";
            }
          }}
        />
      </div>

      {inputs.length > 0 && (
        <>
          <ul className="file-list">
            {inputs.map((inp) => (
              <li key={inp.id}>
                <img
                  className="thumb"
                  alt=""
                  src={inp.thumbUrl ?? HEIC_PLACEHOLDER}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = HEIC_PLACEHOLDER;
                  }}
                />
                <span className="file-name">{inp.file.name}</span>
                <span className="file-size">{formatBytes(inp.file.size)}</span>
                <button
                  className="remove"
                  title="移除"
                  disabled={disabled}
                  onClick={() => {
                    revokeUrl(inp.thumbUrl);
                    onInputsChange(inputs.filter((x) => x.id !== inp.id));
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          <div className="file-list-actions">
            <span>{inputs.length} 個檔案</span>
            <button
              className="danger"
              disabled={disabled}
              onClick={() => {
                teardownInputs(inputs);
                onInputsChange([]);
              }}
            >
              全部清除
            </button>
          </div>
        </>
      )}
    </>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
